import React from 'react';
import { AlertCircle, CheckCircle, XCircle, Upload } from 'lucide-react';
import DocumentUpload from './DocumentUpload';
import { DocumentType } from '../utils/documentUpload';

interface ApplicationStatusProps {
  status: 'pending' | 'approved' | 'rejected';
  comments?: string;
  onDocumentUpload: (type: DocumentType, url: string) => void;
  studentId: string;
}

const ApplicationStatus: React.FC<ApplicationStatusProps> = ({
  status,
  comments,
  onDocumentUpload,
  studentId,
}) => {
  const getStatusColor = () => {
    switch (status) {
      case 'approved':
        return 'bg-green-50 border-green-200 text-green-800';
      case 'rejected':
        return 'bg-red-50 border-red-200 text-red-800';
      default:
        return 'bg-yellow-50 border-yellow-200 text-yellow-800';
    }
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'approved':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'rejected':
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return <AlertCircle className="h-5 w-5 text-yellow-500" />;
    }
  };

  return (
    <div className="space-y-6">
      <div className={`p-4 border rounded-lg ${getStatusColor()}`}>
        <div className="flex items-center space-x-2">
          {getStatusIcon()}
          <h3 className="font-semibold">
            Application Status: {status.charAt(0).toUpperCase() + status.slice(1)}
          </h3>
        </div>
        {comments && (
          <div className="mt-2 text-sm">
            <p className="font-medium">Admin Comments:</p>
            <p className="mt-1">{comments}</p>
          </div>
        )}
      </div>

      {status === 'rejected' && (
        <div className="space-y-4">
          <div className="p-4 bg-white border rounded-lg">
            <h3 className="text-lg font-semibold mb-4">Re-upload Documents</h3>
            <p className="text-sm text-gray-600 mb-6">
              Please review the admin comments and re-upload the required documents. Your application will be reviewed again once you submit the updated documents.
            </p>
            
            <div className="space-y-6">
              <DocumentUpload
                documentType="student_id"
                studentId={studentId}
                label="Student ID"
                description="Clear photo or scan of your student ID card"
                onUploadComplete={(url) => onDocumentUpload('student_id', url)}
                onError={(error) => console.error(error)}
              />

              <DocumentUpload
                documentType="sponsor_id"
                studentId={studentId}
                label="Sponsor ID"
                description="Clear photo or scan of sponsor's ID document"
                onUploadComplete={(url) => onDocumentUpload('sponsor_id', url)}
                onError={(error) => console.error(error)}
              />

              <DocumentUpload
                documentType="proof_of_residence"
                studentId={studentId}
                label="Proof of Residence"
                description="Recent utility bill or bank statement showing residential address"
                onUploadComplete={(url) => onDocumentUpload('proof_of_residence', url)}
                onError={(error) => console.error(error)}
              />

              <DocumentUpload
                documentType="university_registration"
                studentId={studentId}
                label="University Registration"
                description="Proof of registration or acceptance letter"
                onUploadComplete={(url) => onDocumentUpload('university_registration', url)}
                onError={(error) => console.error(error)}
              />

              <DocumentUpload
                documentType="sponsor_payslips"
                studentId={studentId}
                label="Sponsor Payslips"
                description="Last 3 months' payslips"
                onUploadComplete={(url) => onDocumentUpload('sponsor_payslips', url)}
                onError={(error) => console.error(error)}
              />

              <DocumentUpload
                documentType="sponsor_bank_statements"
                studentId={studentId}
                label="Sponsor Bank Statements"
                description="Last 3 months' bank statements"
                onUploadComplete={(url) => onDocumentUpload('sponsor_bank_statements', url)}
                onError={(error) => console.error(error)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApplicationStatus;